import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCloud } from '@fortawesome/free-solid-svg-icons';
import { faAws } from '@fortawesome/free-brands-svg-icons';
import { useAwsConnections, useAwsConnection, AwsConnectionData } from '../hooks/useAwsConnections';
import { useTenants } from '../hooks/useTenants';
import { AwsConnectionEdit } from './AwsConnectionEdit';
import DetailsEditor from './DetailsEditor';
import GlossaryPanel from './GlossaryPanel';
import { useGlossary } from '../hooks/useGlossary';
import styles from './GitHubConfiguration.module.scss';
import '../styles/shared.scss';

const AwsConnections: React.FC = () => {
    const { connectionId } = useParams<{ connectionId?: string }>();
    const navigate = useNavigate();
    const { data: tenants = [] } = useTenants();
    const { isOpen, scrollToItem, openGlossary, closeGlossary } = useGlossary();
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState(300);
    const isResizingRef = useRef(false);

    // Get AWS connections for first tenant (simplified for hooks compliance)
    const firstTenant = tenants[0];
    const { data: connections = [], isLoading, error } = useAwsConnections();
    const allConnections = connections.map((conn: AwsConnectionData) => ({ 
        ...conn, 
        tenantName: firstTenant?.name || 'Unknown' 
    })).sort((a: any, b: any) => a.name.localeCompare(b.name));

    const getTenantName = (tenantId: string) => {
        const tenant = tenants.find(t => t.id === tenantId);
        return tenant?.name || tenantId;
    };

    const handleCreateConnection = () => {
        navigate('/connections/aws');
        setShowCreateForm(true);
    };

    const getConnectionMeta = (connection: AwsConnectionData) => {
        return [
            getTenantName(connection.tenantId),
            connection.accessMethod === 'cross-account-role' ? 'Cross-Account Role' : 'Access Keys',
            connection.region
        ];
    };

    const handleConnectionSelect = (connection: AwsConnectionData) => {
        const connectionKey = `aws-${connection.tenantId}-${connection.id}`;
        navigate(`/connections/aws/${connectionKey}`);
        setShowCreateForm(false);
    };

    const handleFormComplete = () => {
        setShowCreateForm(false);
        navigate('/connections/aws');
    };



    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizingRef.current) return;
        const newWidth = Math.max(250, Math.min(600, e.clientX));
        setSidebarWidth(newWidth);
    }, []);

    const handleMouseUp = useCallback(() => {
        isResizingRef.current = false;
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        isResizingRef.current = true;
        e.preventDefault();
    };

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    // Find selected connection from URL parameter
    const selectedConnectionFromList = connectionId ? allConnections.find((conn: any) => {
        const connectionKey = `aws-${conn.tenantId}-${conn.id}`;
        return connectionKey === connectionId;
    }) || null : null;

    // Fetch individual connection details when selected
    const { data: selectedConnectionDetails } = useAwsConnection(
        selectedConnectionFromList?.id || ''
    );

    // Use detailed connection data if available, otherwise use list data
    const selectedConnection = selectedConnectionDetails || selectedConnectionFromList;

    return (
        <div className={styles.container}>
            {/* Left Column - Connections List */}
            <div 
                className={styles.sidebar} 
                style={{ width: `${sidebarWidth}px` }}
            >
                <div className={styles.header}>
                    <div className="page-title-group">
                        <h3>
                            <FontAwesomeIcon icon={faAws} style={{ marginRight: '8px' }} />
                            AWS
                        </h3>
                        <button
                            onClick={() => openGlossary('AWS Connection')}
                            className="info-button"
                            title="View definition"
                        >
                            Info
                        </button>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            onClick={handleCreateConnection}
                            className="create-button"
                            title="Add AWS Connection"
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </button>
                    </div>
                </div>
                
                <div>
                    {isLoading ? (
                        <p className={styles.statusText}>
                            Loading connections...
                        </p>
                    ) : error ? (
                        <p className={styles.errorText}>
                            Error loading connections
                        </p>
                    ) : allConnections.length === 0 ? (
                        <p className={styles.statusText}>
                            No connections configured
                        </p>
                    ) : (
                        allConnections.map((connection: any) => (
                            <div
                                key={`aws-${connection.tenantId}-${connection.id}`}
                                onClick={() => handleConnectionSelect(connection)}
                                className={`${styles.connectionItem} ${selectedConnection === connection ? styles.selected : ''}`}
                            >
                                <div className={styles.connectionHeader}>
                                    <FontAwesomeIcon 
                                        icon={faCloud} 
                                        className={styles.accessMethodIcon}
                                    />
                                    <div className={styles.connectionName}>
                                        {connection.name}
                                    </div>
                                    <div className={`${styles.status} ${connection.isActive ? styles.active : styles.inactive}`}>
                                        {connection.isActive ? 'Active' : 'Inactive'}
                                    </div>
                                </div>
                                <div className={styles.connectionMeta}>
                                    {getConnectionMeta(connection).map((meta, i) => (
                                        <div key={i}>{meta}</div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Resize Handle */}
            <div 
                className={styles.resizeHandle}
                onMouseDown={handleMouseDown}
            />

            {/* Right Column - Details/Form */}
            <div className={styles.content}>
                {showCreateForm ? (
                    <DetailsEditor
                        title="Create AWS Connection"
                        showDelete={false}
                        onComplete={handleFormComplete}
                    >
                        <AwsConnectionEdit />
                    </DetailsEditor>
                ) : selectedConnection ? (
                    <DetailsEditor
                        title="AWS Connection Details"
                        onComplete={handleFormComplete}
                    >
                        <AwsConnectionEdit
                            connection={selectedConnection}
                        />
                    </DetailsEditor>
                ) : (
                    <div className={styles.placeholder}>
                        Select an AWS connection or create a new one
                    </div>
                )}
            </div>
            
            <GlossaryPanel 
                isOpen={isOpen} 
                onClose={closeGlossary} 
                scrollToItem={scrollToItem} 
            />
        </div>
    );
};

export default AwsConnections;